import React, { useState, useEffect } from 'react';
import './CatalogueUpdateScreen.css'
import * as XLSX from 'xlsx';
import { FaTrashAlt } from 'react-icons/fa';
import { FaUpload } from 'react-icons/fa';

const CatalogueUpdateScreen = () => {
    const [catalogues, setCatalogues] = useState([]);
    const [selectedCatalogue, setSelectedCatalogue] = useState(null);
    const [excelFile, setExcelFile] = useState(null);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            alert('Please log in to manage catalogue');
            window.location.href = '#/LoginPage';
            return;
        }
        const url = "https://08yvli9kn1.execute-api.us-east-1.amazonaws.com/api/products"
        try {
            fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            })
                .then((response) => response.json())
                .then((data) => { setCatalogues(data.data) });
        } catch (ex) {
            console.log(ex)
        }
    }, []);

    // Price changes are now per-row; use updateProduct(index, 'NewPrice', value)

    const handleExcelUpload = (event) => {
        setExcelFile(event.target.files[0]);
    };

    const handleUpdateCatalogue = () => {
        const token = localStorage.getItem('token');
        // Prepare updates: include ItemID and the new price (if set) and any uploaded image info
        const updates = catalogues.map(c => ({
            ItemID: c.ItemID,
            NewPrice: c.NewPrice ?? c.BasePrice,
            imageUploaded: c.imageUploaded || null,
        }));

        fetch(`https://08yvli9kn1.execute-api.us-east-1.amazonaws.com/api/products`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ updates }),
        })
            .then(response => response.json())
            .then(data => console.log(data))
            .catch(error => console.error(error));
    };

    const handleUpdateRow = (index) => {
        const token = localStorage.getItem('token');

        if (!token) {
            alert('Please log in to manage catalogue');
            window.location.href = '#/LoginPage';
            return;
        }
        const catalogue = catalogues[index];
        const isNew = catalogue.isNew;

        if (isNew) {
            // Create new product
            const newProductData = {
                ItemID: catalogue.ItemID,
                Description: catalogue.Description,
                BasePrice: parseFloat(catalogue.NewPrice),
                Category: catalogue.Category,
                Stock: parseInt(catalogue.Stock),
                image: catalogue.imageUploaded || ''
            };

            fetch(`https://08yvli9kn1.execute-api.us-east-1.amazonaws.com/api/products`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(newProductData),
            })
                .then(response => response.json())
                .then(data => {
                    if (data.success) {
                        console.log('Product created successfully:', data);
                        // Update the catalogue with the new ID and mark as not new
                        updateProduct(index, 'ItemID', data.data.ItemID);
                        updateProduct(index, 'isNew', false);
                        alert(`Product created successfully with ID: ${data.data.ItemID}!`);
                    } else {
                        throw new Error(data.error || 'Failed to create product');
                    }
                })
                .catch(error => {
                    console.error('Error creating product:', error);
                    alert(`Error creating product: ${error.message}`);
                });
        } else {
            // Update existing product
            const update = {
                ItemID: catalogue.ItemID,
                Description: catalogue.Description,
                BasePrice: catalogue.NewPrice ? parseFloat(catalogue.NewPrice) : parseFloat(catalogue.BasePrice),
                Category: catalogue.Category,
                Stock: parseInt(catalogue.Stock),
                image: catalogue.imageUploaded || ''
            };

            fetch(`https://08yvli9kn1.execute-api.us-east-1.amazonaws.com/api/products/${catalogue.ItemID}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(update),
            })
                .then(response => response.json())
                .then(data => {
                    if (!data.success && data.error == "Unauthorized") {
                        alert('Session expired, Please log in again to manage catalogue');
                        localStorage.removeItem('token');
                        window.location.href = '#/LoginPage';
                        return
                    }
                    console.log('Row updated successfully:', data);
                    alert(`Item ${catalogue.ItemID} updated successfully!`);
                })
                .catch(error => {
                    console.error('Error updating row:', error);
                    alert(`Error updating item ${catalogue.ItemID}`);
                });
        }
    };

    const handleDeleteRow = (index) => {
        const token = localStorage.getItem('token');

        if (!token) {
            alert('Please log in to manage catalogue');
            localStorage.removeItem('token');
            window.location.href = '#/LoginPage';
            return;
        }

        const catalogue = catalogues[index];
        const isNew = catalogue.isNew;

        if (isNew) {
            // Remove from local state without API call
            const newCatalogues = [...catalogues];
            newCatalogues.splice(index, 1);
            setCatalogues(newCatalogues);
            alert('New product removed.');
        } else {
            // Delete from backend
            fetch(`https://08yvli9kn1.execute-api.us-east-1.amazonaws.com/api/products/${catalogue.ItemID}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            })
                .then(response => response.json())
                .then(data => {
                    if (data.success) {
                        console.log('Product deleted successfully:', data);
                        // Remove from local state
                        const newCatalogues = [...catalogues];
                        newCatalogues.splice(index, 1);
                        setCatalogues(newCatalogues);
                        alert(`Item ${catalogue.ItemID} deleted successfully!`);
                    } else {
                        if (!data.success && data.error == "Unauthorized") {
                            alert('Session expired, Please log in again to manage catalogue');
                            localStorage.removeItem('token');
                            window.location.href = '#/LoginPage';
                            return
                        }
                    }
                })
                .catch(error => {
                    console.error('Error deleting product:', error);
                    alert(`Error deleting item ${catalogue.ItemID}: ${error.message}`);
                });
        }
    };

    const handleUploadExcel = () => {
        if (!excelFile) {
            alert('Please select an Excel file first.');
            return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);
            // Assume columns: ItemID, Description, BasePrice, Category, Stock
            const products = jsonData.map(row => ({
                ItemID: row.ItemID || row['Item ID'] || `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                Description: row.Description || row['Product Name'] || '',
                BasePrice: parseFloat(row.BasePrice || row['Price'] || 0),
                Category: row.Category || 'General',
                Stock: parseInt(row.Stock || row['Quantity'] || 0)
            }));
            // Send to batch endpoint
            const token = localStorage.getItem('token');
            fetch('https://08yvli9kn1.execute-api.us-east-1.amazonaws.com/api/products/batch', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ products }),
            })
                .then(response => response.json())
                .then(data => {
                    if (data.success) {
                        alert(`Successfully uploaded ${products.length} products!`);
                        // Refresh the list
                        window.location.reload();
                    } else {
                        alert('Upload failed: ' + data.error);
                    }
                })
                .catch(error => {
                    console.error(error);
                    alert('Upload failed');
                });
        };
        reader.readAsArrayBuffer(excelFile);
    };

    const updateProduct = (index, field, value) => {
        const newProducts = [...catalogues];
        newProducts[index][field] = value;
        setCatalogues(newProducts);
    };

    const handleAddNewProduct = () => {
        const newProduct = {
            ItemID: `new-${Date.now()}`, // Temporary ID; real ID should be assigned by backend
            Description: 'New Product',
            Category: 'NONE',
            BasePrice: 0,
            Stock: 0,
            isNew: true, // Flag to indicate this is a new product
        };
        setCatalogues([...catalogues, newProduct]);
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        window.location.href = '#/LoginPage';
    };
    const handleUploadImage = (index, file) => {
        const token = localStorage.getItem('token');
        if (!file) return;

        // Use the item's ItemID to create a unique filename on the server
        const itemId = catalogues[index]?.ItemID || `item-${index}`;
        const filename = `${itemId}.png`;

        // Convert file to base64
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = reader.result.split(',')[1]; // Remove data:image/...;base64,
            fetch('https://08yvli9kn1.execute-api.us-east-1.amazonaws.com/api/images', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ file: base64, filename }),
            })
                .then((response) => response.json())
                .then((data) => {
                    console.log(data);
                    // Mark the row as having an uploaded image with timestamp for cache busting
                    updateProduct(index, 'imageTimestamp', Date.now());
                    updateProduct(index, 'imageUploaded', data.data.filename || filename);
                })
                .catch((error) => console.error(error));
        };
        reader.readAsDataURL(file);
    }
    const handleDeleteRowImage = (index) => {
        const token = localStorage.getItem('token');
        const catalogue = catalogues[index];
        // if (!catalogue.imageUploaded) {
        //     alert('No image to delete for this item.');
        //     return;
        // }
        fetch(`https://08yvli9kn1.execute-api.us-east-1.amazonaws.com/api/images/${catalogue.ItemID}.png`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        })
            .then(response => response.json())
            .then(data => {
                if (data.success) {
                    console.log('Image deleted successfully:', data);
                    // Remove imageUploaded field and timestamp from the product
                    updateProduct(index, 'imageUploaded', null);
                    updateProduct(index, 'imageTimestamp', Date.now());
                    alert(`Image for Item ${catalogue.ItemID} deleted successfully!`);
                } else {
                    alert(`Error deleting image for Item ${catalogue.ItemID}: ${data.error}`);
                }
            })
            .catch(error => {
                console.error('Error deleting image:', error);
                alert(`Error deleting image for Item ${catalogue.ItemID}: ${error.message}`);
            });
    };

    const handleUploadClick = (index) => {
        const fileInput = document.getElementById(`imageInput-${index}`);
        if (fileInput) {
            fileInput.click();
        }
    };

    return (
        <div className="catalogUpdate">
            <table>
                <thead>
                    <tr>
                        <th>ItemID</th>
                        <th>Description</th>
                        <th>Category</th>
                        <th>Current Price</th>
                        <th>New Price</th>
                        <th>Stock</th>
                        <th>Image Action</th>
                        <th>Row Action</th>
                    </tr>
                </thead>
                <tbody>
                    {catalogues.map((catalogue, index) => {
                        const isNew = catalogue.isNew;
                        return (
                            <tr key={index}>
                                <td>
                                    {isNew ? (
                                        <input
                                            type="text"
                                            value={catalogue.ItemID}
                                            onChange={(e) => updateProduct(index, 'ItemID', e.target.value)}
                                        />
                                    ) : (
                                        catalogue.ItemID
                                    )}
                                </td>

                                <td>
                                    <input
                                        type="text"
                                        value={catalogue.Description}
                                        onChange={(e) => updateProduct(index, 'Description', e.target.value)}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="text"
                                        value={catalogue.Category}
                                        onChange={(e) => updateProduct(index, 'Category', e.target.value)}
                                    />
                                </td>
                                <td>
                                    {isNew ? (
                                        <input
                                            type="number"
                                            value={catalogue.BasePrice}
                                            onChange={(e) => updateProduct(index, 'BasePrice', e.target.value)}
                                        />
                                    ) : (
                                        catalogue.BasePrice
                                    )}
                                </td>
                                <td>
                                    {isNew ? (
                                        0.00
                                    ) : (
                                        <input
                                            type="number"
                                            // bind to a per-row field; if NewPrice not set, show BasePrice
                                            value={catalogue.NewPrice ?? catalogue.BasePrice ?? ''}
                                            onChange={(e) => updateProduct(index, 'NewPrice', e.target.value)}
                                        />)}
                                </td>
                                <td>
                                    <input
                                        type="number"
                                        value={catalogue.Stock}
                                        onChange={(e) => updateProduct(index, 'Stock', e.target.value)}
                                    />
                                </td>
                                <td>
                                    <div className="image-actions">
                                        <img className="item-image" src={`https://dblla03zmq1su.cloudfront.net/${catalogue.ItemID}.png?t=${catalogue.imageTimestamp || Date.now()}`} alt="No Image loaded" loading='lazy' width='100px' height='100px' />
                                        <button className="delete-image-button" onClick={() => handleDeleteRowImage(index)}>
                                            <FaTrashAlt />
                                        </button>
                                        <button className="upload-image-button" onClick={() => handleUploadClick(index)}><FaUpload /></button>
                                        <input
                                            type="file"
                                            id={`imageInput-${index}`}
                                            accept="image/*"
                                            style={{ display: 'none' }}
                                            onChange={(e) => handleUploadImage(index, e.target.files[0])}
                                        />
                                    </div>
                                </td>
                                <td>
                                    <br /> <button className="update-item-button"
                                        onClick={() => handleUpdateRow(index)}>{isNew ? 'Create Item' : 'Update Item'}
                                    </button> <br /><br />
                                    <button className="delete-item-button"
                                        onClick={() => handleDeleteRow(index)}>Delete Item
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table><br /><br />
            <button className="addNewProduct" onClick={handleAddNewProduct}>Add New Product</button><br /><br />
            <input type="file" accept=".xlsx,.xls" onChange={handleExcelUpload} />
            <p>Upload an Excel file with columns: ItemID, Description, BasePrice, Category (optional), Stock (optional)</p>
            <button onClick={handleUploadExcel}>Upload Excel</button>
        </div >
    );
};

export default CatalogueUpdateScreen;